import type { ResolvedAsset, ResolvedAssets } from '@/types/api/assets';

export type DisplayFile =
  | {
      type?: string;
      url?: string;
      viewUrl?: string;
      thumbnailUrl?: string;
      blobstore?: { id?: string };
    }
  | undefined;

export type ThumbnailSource = { kind: 'image'; src: string } | { kind: 'firstFrame'; src: string } | { kind: 'none' };

export type ThumbnailOptions = {
  type?: string;
  thumbnailOnly?: boolean;
};

/**
 * Returns the raw asset URL (Blobstore signed URL or Cloudinary URL).
 */
export function assetUrl(file: DisplayFile, resolved: ResolvedAssets): string | undefined {
  const asset = resolvedAsset(file, resolved);
  return asset?.signedUrl || file?.url;
}

/**
 * Returns the optimized view URL (WebP variant when available).
 */
export function assetViewUrl(file: DisplayFile, resolved: ResolvedAssets): string | undefined {
  const asset = resolvedAsset(file, resolved);
  return asset?.signedWebpUrl || asset?.signedUrl || file?.viewUrl || file?.url;
}

/**
 * Returns a dedicated thumbnail URL.
 */
export function assetThumbnailUrl(
  file: DisplayFile,
  resolved: ResolvedAssets,
  options: ThumbnailOptions = {},
): string | undefined {
  const asset = resolvedAsset(file, resolved);
  const fileType = options.type ?? file?.type;

  const fallbackToFullSize = !options.thumbnailOnly && fileType === 'image';

  if (asset) {
    if (asset.signedThumbnailUrl) {
      return asset.signedThumbnailUrl;
    }
    return fallbackToFullSize ? (asset.signedWebpUrl ?? asset.signedUrl) : undefined;
  }

  if (file?.thumbnailUrl) {
    return file.thumbnailUrl;
  }

  return fallbackToFullSize ? file?.url : undefined;
}

/**
 * What a media tile renders: a video's own file, so the tile can play it, or an image's thumbnail.
 */
export function assetPreviewUrl(file: DisplayFile, resolved: ResolvedAssets): string | undefined {
  if (file?.type === 'video') {
    return assetUrl(file, resolved);
  }
  return file?.type === 'image' ? assetThumbnailUrl(file, resolved) : undefined;
}

/**
 * High-level helper for UI components rendering media thumbs.
 * Handles the distinction between <img> sources and <video#t=0.001> frame previews.
 */
export function thumbnailSource(
  file: DisplayFile,
  resolved: ResolvedAssets,
  options: ThumbnailOptions = {},
): ThumbnailSource {
  const thumbnail = assetThumbnailUrl(file, resolved, options);

  if (thumbnail) {
    return { kind: 'image', src: thumbnail };
  }

  const asset = resolvedAsset(file, resolved);
  const fileType = options.type ?? file?.type;

  // For blobstore video thumbnail, fallback to video first frame
  if (asset && fileType === 'video') {
    return { kind: 'firstFrame', src: asset.signedUrl };
  }

  return { kind: 'none' };
}

export function isBlobstoreResolved(file: DisplayFile, resolved: ResolvedAssets): boolean {
  return Boolean(resolvedAsset(file, resolved));
}

/** Internal helper */
function resolvedAsset(file: DisplayFile, resolved: ResolvedAssets): ResolvedAsset | undefined {
  const assetId = file?.blobstore?.id;
  return assetId ? resolved[assetId] : undefined;
}

export function firstFrameSrc(url: string): string {
  return url.includes('#') ? url : `${url}#t=0.001`;
}
